import { pickRateClosestToPrice } from 'analytics/metasearchPriceAccuracyTracking'
import { min } from 'lib/array/arrayUtils'
import { mapBedbankBoardCode } from 'lib/hotels/bedbankUtils'
import type { GhaPriceMatchClickThrough } from 'lib/metasearch/ghaPriceMatch'

export function parseAmount(value: unknown): number | undefined {
  // An empty param is Google telling us nothing, not telling us zero — `Number('')` would read it
  // as a legitimate 0 and let a breakdown through on figures we never received.
  if (typeof value !== 'string' || !value.trim()) {
    return undefined
  }
  const amount = Number(value)
  return Number.isFinite(amount) ? amount : undefined
}

/** Google's displayed total, only when it is in the currency our prices are in. */
function comparableGooglePrice(gha: GhaPriceMatchClickThrough, currency: string): number | undefined {
  return gha.googleCurrency === currency && gha.googlePrice && gha.googlePrice > 0 ? gha.googlePrice : undefined
}

/** How far `price` sits above (+) or below (−) Google's price, in %, to 2 decimal places. */
export function getGapToGooglePct(
  price: number,
  currency: string,
  gha: GhaPriceMatchClickThrough | undefined,
): number | undefined {
  const googlePrice = gha && comparableGooglePrice(gha, currency)
  if (!googlePrice) return undefined
  // Whole cents, so float sums like 789.1600000000001 don't show up as a gap.
  const priceCents = Math.round(price * 100)
  const googleCents = Math.round(googlePrice * 100)
  return Math.round((priceCents - googleCents) * 10000 / googleCents) / 100
}

interface RateReader<Rate> {
  roomId: (rate: Rate) => string
  refundable: (rate: Rate) => boolean
  price: (rate: Rate) => number
}

/**
 * The rate standing in for Google's offer: in Google's room with Google's refundability, the one
 * closest to Google's price. Without a comparable Google price, the cheapest of them.
 */
export function pickGoogleRoomRate<Rate>(
  rates: Array<Rate>,
  gha: GhaPriceMatchClickThrough,
  currency: string,
  read: RateReader<Rate>,
): Rate | undefined {
  const googleRoomRates = rates.filter(rate => read.roomId(rate) === gha.roomId && read.refundable(rate) === gha.refundable)
  const googlePrice = comparableGooglePrice(gha, currency)
  if (googlePrice) {
    return pickRateClosestToPrice(googleRoomRates.map(rate => ({ rate, price: read.price(rate) })), googlePrice)?.rate
  }
  return min(googleRoomRates, read.price)
}

/**
 * The rate the metasearch landing page leads with. On a price-matched click, Google's offer: the
 * rate in Google's room, board and refundability (the one closest to Google's price if several).
 * Otherwise the cheapest rate: no price match, no board in the token, or we don't sell Google's offer.
 */
export function pickMetasearchTopRate(
  rates: Array<App.BedbankRate>,
  gha: GhaPriceMatchClickThrough | undefined,
  currency: string,
): App.BedbankRate | undefined {
  const cheapestRate = min(rates, rate => rate.totals.total)
  if (!gha?.boardCode) return cheapestRate

  const googleBoardRates = rates.filter(rate => rate.boardCode === mapBedbankBoardCode(gha.boardCode))
  const googleRate = pickGoogleRoomRate(googleBoardRates, gha, currency, {
    roomId: rate => rate.roomId,
    refundable: rate => rate.refundable,
    price: rate => rate.totals.total,
  })
  return googleRate ?? cheapestRate
}
